import { Injectable, signal } from '@angular/core';

/**
 *  Aliases for date-fns formatting patterns
 *  @variation DATE_SHORT - 04.29.1453
 *  @variation DATE_FULL - April 29th, 1453
 *  @variation DATE_NAMED_MONTH - 4 Sep 1453
 *  @variation TIME_SHORT - 14:30 or 02:30 AM
 *  @variation DAY_TIME - Mon 14:30 or Mon 02:30 AM
 *  @variation TIME_SHORT_24 - 24:00 or TIME_SHORT
 *  @variation DAY_TIME_24 - Mon 24:00 or DAY_TIME
 *  @variation DATE_TIME_SHORT - 04/3/2023, 19:02
 *  @variation DATE_TIME_NAMED_MONTH - 5 Aug 2023, 19:02
 *  @variation DAY_MONTH_TIME - 5 Aug 19:02
 */
export type DateFormat =
  | 'DATE_SHORT'
  | 'DATE_FULL'
  | 'DATE_NAMED_MONTH'
  | 'TIME_SHORT'
  | 'DAY_TIME'
  | 'TIME_SHORT_24'
  | 'DAY_TIME_24'
  | 'TIME_DATE_SHORT'
  | 'DATE_TIME_LONG'
  | 'DATE_TIME_SHORT'
  | 'DATE_TIME_NAMED_MONTH'
  | 'DAY_MONTH_TIME';

export const DATE_FORMATS = [
  'DATE_SHORT',
  'DATE_FULL',
  'DATE_NAMED_MONTH',
  'TIME_SHORT',
  'DAY_TIME',
  'TIME_DATE_SHORT',
  'DATE_TIME_LONG',
  'DATE_TIME_SHORT',
  'DATE_TIME_NAMED_MONTH',
  'DAY_MONTH_TIME',
];

export const DATE_24_PATTERN = new Map<DateFormat, string>([
  ['DATE_SHORT', 'dd.MM.yyyy'],
  ['DATE_FULL', 'PPP'],
  ['DATE_NAMED_MONTH', 'd LLL yyyy'],
  ['TIME_SHORT', 'HH:mm'],
  ['DAY_TIME', 'eee HH:mm'],
  ['TIME_SHORT_24', 'kk:mm'],
  ['DAY_TIME_24', 'eee kk:mm'],
  ['TIME_DATE_SHORT', 'HH:mm P'],
  ['DAY_MONTH_TIME', 'd LLL HH:mm'],
  ['DATE_TIME_LONG', 'eee, PPP, HH:mm'],
  ['DATE_TIME_SHORT', 'd.MM.yyyy HH:mm'],
  ['DATE_TIME_NAMED_MONTH', 'd LLL yyyy, HH:mm'],
]);

export const DATE_12_PATTERN = new Map<DateFormat, string>([
  ['DATE_SHORT', 'MM/d/yyyy'],
  ['DATE_FULL', 'PPP'],
  ['DATE_NAMED_MONTH', 'd LLL yyyy'],
  ['TIME_SHORT', 'KK:mm a'],
  ['DAY_TIME', 'eee KK:mm a'],
  ['TIME_SHORT_24', 'hh:mm a'],
  ['DAY_TIME_24', 'eee hh:mm a'],
  ['TIME_DATE_SHORT', 'KK:mm a P'],
  ['DAY_MONTH_TIME', 'd LLL KK:mm a'],
  ['DATE_TIME_LONG', 'eee, PPP, KK:mm a'],
  ['DATE_TIME_SHORT', 'MM/d/yyyy, KK:mm a'],
  ['DATE_TIME_NAMED_MONTH', 'd LLL yyyy, KK:mm a'],
]);

export function isDateFormat(format: string): boolean {
  return DATE_FORMATS.includes(format);
}

@Injectable({ providedIn: 'root' })
export class SntDateService {
  private _patterns: Map<DateFormat, string> = DATE_24_PATTERN;

  readonly timeZone = signal<string | null>(null);
  readonly _is24Format = signal(true);

  set is24Format(value: Readonly<boolean>) {
    this._is24Format.set(value);
    this._patterns = value ? DATE_24_PATTERN : DATE_12_PATTERN;
  }

  get is24Format() {
    return this._is24Format();
  }

  get patterns() {
    return this._patterns;
  }

  updateSettings(data: { is24Format?: boolean; timeZone?: string | null }): void {
    if (data.is24Format !== undefined) {
      this.is24Format = data.is24Format;
    }

    if (data.timeZone !== undefined) {
      this.timeZone.set(data.timeZone);
    }
  }
}
